import { useState, useEffect } from 'react'
import {
  X,
  Lock,
  Users,
  Link2,
  Check,
  Loader2,
  Trash2,
  Globe,
} from 'lucide-react'
import {
  listAppShares,
  normalizeAccessLevel,
  shareApp,
  removeAppShare,
  updateSharePermission,
  updateApp,
  requestPublicAccess,
  getPublicAccessRequestStatus,
  type App,
  type AppShare,
  type PublicAccessRequestStatus,
} from '../lib/api'
import { withMountBase } from '../lib/mountBase'
import { useUser } from '../hooks/useUser'
import { StyledSelect } from './ui/StyledSelect'
import TransferOwnershipSection from './TransferOwnershipSection'

interface ShareModalProps {
  app: App
  onClose: () => void
  onShared?: () => void
}

function getAccessLevelOptions(isPublished: boolean, isAdmin: boolean) {
  const options: Array<{
    value: string
    label: string
    description: string
    icon: typeof Lock
    disabled?: boolean
  }> = [
    { value: 'private', label: 'Private', description: 'You and people you add below', icon: Lock },
    { value: 'link', label: 'Anyone with the link', description: 'Any platform user with the link', icon: Link2 },
    {
      value: 'platform',
      label: 'Platform',
      description: isPublished
        ? 'Visible to all platform users in Browse Tab'
        : 'Publish the app first to make it visible in Browse Tab',
      icon: Users,
      disabled: !isPublished,
    },
    {
      value: 'public',
      label: 'Public (no login)',
      description: isAdmin
        ? 'Anyone on the internet — no platform account required'
        : 'Request admin approval to allow anyone (no login) to access this app',
      icon: Globe,
    },
  ]
  return options
}

export default function ShareModal({ app, onClose, onShared }: ShareModalProps) {
  const user = useUser()
  const [shares, setShares] = useState<AppShare[]>([])
  const [sharesLoading, setSharesLoading] = useState(true)
  const [email, setEmail] = useState('')
  const [permission, setPermission] = useState('view')
  const [sharing, setSharing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [removing, setRemoving] = useState<string | null>(null)
  const [currentAccessLevel, setCurrentAccessLevel] = useState(normalizeAccessLevel(app.access_level))
  const [accessLevelLoading, setAccessLevelLoading] = useState(false)
  const [copied, setCopied] = useState(false)
  // Public-access request flow (non-admin owners): shows justification dialog
  // and surfaces existing pending/rejected request state on re-open.
  const [publicRequest, setPublicRequest] = useState<PublicAccessRequestStatus | null>(null)
  const [showPublicRequestDialog, setShowPublicRequestDialog] = useState(false)
  const [publicJustification, setPublicJustification] = useState('')
  const [submittingPublicRequest, setSubmittingPublicRequest] = useState(false)

  const canManageShares = app.my_permission === 'owner' || app.my_permission === 'edit' || app.my_permission === 'admin'
  // Giving the app away is an owner-only action (admins can act on any app).
  const canTransfer = app.my_permission === 'owner' || user.isAdmin

  useEffect(() => {
    loadShares()
    if (canManageShares) {
      loadPublicRequest()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app.name])

  async function loadShares() {
    try {
      const data = await listAppShares(app.name)
      setShares(data)
    } catch {
      // silently fail for non-owners
    } finally {
      setSharesLoading(false)
    }
  }

  async function loadPublicRequest() {
    try {
      const data = await getPublicAccessRequestStatus(app.name)
      setPublicRequest(data.request)
    } catch {
      // non-owners get 403; silently ignore
    }
  }

  async function handleAccessLevelChange(newLevel: string) {
    // Non-admins selecting 'public' route through the request/approval flow
    // instead of an immediate UPDATE (backend would 403 anyway). Owners with
    // admin rights flip directly.
    if (newLevel === 'public' && !user.isAdmin && currentAccessLevel !== 'public') {
      setShowPublicRequestDialog(true)
      setPublicJustification('')
      return
    }

    setAccessLevelLoading(true)
    setError(null)
    const prevLevel = currentAccessLevel
    setCurrentAccessLevel(newLevel)
    try {
      await updateApp(app.name, { access_level: newLevel } as Partial<App>)
      onShared?.()
    } catch (err) {
      setCurrentAccessLevel(prevLevel)
      setError(err instanceof Error ? err.message : 'Failed to update access level')
    } finally {
      setAccessLevelLoading(false)
    }
  }

  async function handleSubmitPublicRequest() {
    setSubmittingPublicRequest(true)
    setError(null)
    try {
      const result = await requestPublicAccess(
        app.name,
        publicJustification.trim() || undefined,
      )
      setShowPublicRequestDialog(false)
      // Refresh the request status so the pending pill renders.
      if (result.status === 'already_public') {
        setCurrentAccessLevel('public')
      } else {
        await loadPublicRequest()
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit request')
    } finally {
      setSubmittingPublicRequest(false)
    }
  }

  async function handleShare(e: React.FormEvent) {
    e.preventDefault()
    setSharing(true)
    setError(null)
    try {
      await shareApp(app.name, email, permission)
      setEmail('')
      await loadShares()
      onShared?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to share app')
    } finally {
      setSharing(false)
    }
  }

  async function handleUpdatePermission(shareEmail: string, newPerm: string) {
    setError(null)
    try {
      await updateSharePermission(app.name, shareEmail, newPerm)
      await loadShares()
      onShared?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update permission')
    }
  }

  async function handleRemove(shareEmail: string) {
    setRemoving(shareEmail)
    try {
      await removeAppShare(app.name, shareEmail)
      await loadShares()
      onShared?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove share')
    } finally {
      setRemoving(null)
    }
  }

  function handleCopyLink() {
    const appPath = withMountBase(`/apps/${app.name}`, window.location.pathname)
    const url = `${window.location.origin}${appPath}`
    navigator.clipboard.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-xl font-semibold">Share &ldquo;{app.display_name || app.name}&rdquo;</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-secondary transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-sm text-muted-foreground mb-5">Control who can access your app</p>

        {/* Access Level Selector */}
        {canManageShares && (
          <div className="mb-5">
            <p className="text-sm font-medium mb-2">General access</p>
            <StyledSelect
              value={currentAccessLevel}
              onChange={handleAccessLevelChange}
              disabled={accessLevelLoading}
              options={getAccessLevelOptions(
                app.production_status === 'running' || (app.production_status == null && app.access_level === 'platform'),
                user.isAdmin,
              )}
              className="w-full"
            />
            {currentAccessLevel === 'link' && (
              <p className="text-xs text-muted-foreground mt-1.5">
                Visible to anyone with the link (<em>not visible in Browse Tab</em>).
              </p>
            )}
            {currentAccessLevel === 'platform' && (
              <p className="text-xs text-muted-foreground mt-1.5">
                Visible to all platform users in Browse Tab
              </p>
            )}
            {currentAccessLevel === 'public' && (
              <p className="text-xs text-muted-foreground mt-1.5">
                Anyone on the internet can open this app without a login.
              </p>
            )}
            {publicRequest && currentAccessLevel !== 'public' && (
              <div className="mt-2 flex items-start gap-2">
                {publicRequest.status === 'pending' && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 text-xs font-medium">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Public access request pending admin review
                  </span>
                )}
                {publicRequest.status === 'rejected' && (
                  <span className="inline-flex flex-col px-2 py-1 rounded-md bg-destructive/10 text-destructive text-xs">
                    <span className="font-medium">Public access request rejected</span>
                    {publicRequest.review_note && (
                      <span className="text-xs mt-0.5 opacity-80">Note: {publicRequest.review_note}</span>
                    )}
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Public-access request dialog (non-admin owners) */}
        {showPublicRequestDialog && (
          <div className="mb-5 p-4 rounded-lg border border-border bg-secondary/40">
            <p className="text-sm font-medium mb-1">Request public access</p>
            <p className="text-xs text-muted-foreground mb-3">
              An admin will review your request. You can include a short justification (optional, max 2000 chars).
            </p>
            <textarea
              value={publicJustification}
              onChange={(e) => setPublicJustification(e.target.value)}
              placeholder="e.g. Public marketing landing page; team demo for external partners; documentation site that should be linkable from outside the platform."
              rows={3}
              maxLength={2000}
              className="w-full px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm mb-2"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowPublicRequestDialog(false)}
                disabled={submittingPublicRequest}
                className="px-3 py-1.5 rounded-lg border border-border hover:bg-secondary text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitPublicRequest}
                disabled={submittingPublicRequest}
                className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 text-sm inline-flex items-center gap-1.5"
              >
                {submittingPublicRequest ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Submit request
              </button>
            </div>
          </div>
        )}

        {/* Copy link */}
        <div className="mt-3 mb-5">
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopyLink}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors flex-1 text-sm"
            >
              {copied ? <Check className="w-4 h-4 text-green-500" /> : <Link2 className="w-4 h-4" />}
              {copied ? 'Link copied!' : 'Copy link'}
            </button>
          </div>
          {currentAccessLevel === 'private' && (
            <p className="text-xs text-muted-foreground mt-1.5">
              Only people with access can use this link
            </p>
          )}
        </div>

        {/* Share with specific people */}
        {canManageShares && (
          <div className="border-t border-border pt-4 mb-4">
            <p className="text-sm font-medium mb-3">Share with specific people</p>
            <form onSubmit={handleShare} className="flex gap-2 mb-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@example.com"
                className="flex-1 px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
              />
              <StyledSelect
                value={permission}
                onChange={setPermission}
                options={[
                  { value: 'view', label: 'Viewer' },
                  { value: 'edit', label: 'Editor' },
                ]}
              />
              <button
                type="submit"
                disabled={sharing || !email}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 text-sm"
              >
                {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
              </button>
            </form>
          </div>
        )}

        {error && (
          <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm mb-4">
            {error}
          </div>
        )}

        {/* Shares List with per-user role dropdowns */}
        <div className="space-y-2 mb-5">
          {sharesLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : shares.length === 0 ? (
            <p className="text-center text-muted-foreground py-3 text-sm">
              No individual shares yet
            </p>
          ) : (
            shares.map((share) => (
              <div
                key={share.id}
                className="flex items-center justify-between p-3 rounded-lg bg-secondary"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm truncate">{share.email}</div>
                  {!share.user_registered && (
                    <div className="text-xs text-yellow-500">Pending invite</div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {canManageShares ? (
                    <>
                      <StyledSelect
                        value={share.permission}
                        onChange={(newPerm) => handleUpdatePermission(share.email, newPerm)}
                        options={[
                          { value: 'view', label: 'Viewer' },
                          { value: 'edit', label: 'Editor' },
                        ]}
                      />
                      <button
                        onClick={() => handleRemove(share.email)}
                        disabled={removing === share.email}
                        className="p-1.5 rounded hover:bg-background text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
                      >
                        {removing === share.email ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </>
                  ) : (
                    <span className="text-xs text-muted-foreground capitalize">{share.permission}</span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Transfer ownership (owner/admin only) — collapsed by default */}
        {canTransfer && (
          <div className="border-t border-border pt-4 mb-5">
            <TransferOwnershipSection
              app={app}
              canTransfer={canTransfer}
              onChanged={loadShares}
            />
          </div>
        )}

        <button
          onClick={onClose}
          className="w-full px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors text-sm"
        >
          Done
        </button>
      </div>
    </div>
  )
}
